import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabaseV2 } from "./supabase";
import { useSeasonScope } from "./seasons";
import { withMissingResponses } from "./attendance";
import type {
  Match,
  MatchPlayerStat,
  Player,
  Session,
  SessionResponse,
  SquadScoreSnapshot,
} from "../types";

export async function fetchAllPages(createQuery: (from: number, to: number) => any) {
  const pageSize = 1000;
  const rows: any[] = [];

  for (let from = 0; ; from += pageSize) {
    const to = from + pageSize - 1;
    const { data, error } = await createQuery(from, to);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }

  return rows;
}

/**
 * Dati della squadra usati da Home, Statistiche, Rosa e scheda giocatore.
 * Di default filtrati sulla stagione selezionata; `seasonId` forza una
 * stagione precisa, `allSeasons` toglie il filtro. Le partite escluse dalle
 * statistiche (matches.counts_for_stats = false) non compaiono in `matches`
 * ne' in `matchStats`.
 */
export function useWorkspaceData(
  teamId: string,
  options?: { seasonId?: string | null; allSeasons?: boolean; competitionId?: string | null },
) {
  const scope = useSeasonScope(options);
  // Filtro per competizione: riguarda partite e statistiche partita. Le
  // risposte presenza sono per sessione (una giornata puo' contenere partite
  // di competizioni diverse) e restano di stagione.
  const competitionId = options?.competitionId || null;
  const { seasonId } = scope;
  const enabled = scope.ready;

  const playersQuery = useQuery({
    queryKey: ["players", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.from("players").select("*").eq("team_id", teamId).order("last_name");
      if (error) throw error;
      return (data || []) as Player[];
    },
  });
  const eventsQuery = useQuery({
    queryKey: ["events", teamId, "all", scope.key],
    enabled,
    queryFn: async () => {
      let query = supabaseV2.from("sessions").select("*").eq("team_id", teamId);
      if (seasonId) query = query.eq("season_id", seasonId);
      const { data, error } = await query.order("starts_at");
      if (error) throw error;
      return (data || []) as Session[];
    },
  });
  const responsesQuery = useQuery({
    queryKey: ["responses", teamId, scope.key],
    enabled,
    queryFn: async () => {
      return fetchAllPages((from, to) => {
        let query = supabaseV2
          .from("session_responses")
          .select("id, session_id, player_id, status, source, sessions!inner(team_id, starts_at, title, has_matches, season_id)")
          .eq("sessions.team_id", teamId);
        if (seasonId) query = query.eq("sessions.season_id", seasonId);
        return query.range(from, to);
      }).then((rows) =>
        rows.map((row: any) => ({
          id: row.id,
          session_id: row.session_id,
          player_id: row.player_id,
          status: row.status,
          source: row.source,
          session_starts_at: row.sessions?.starts_at || null,
          session_title: row.sessions?.title || null,
          session_has_matches: !!row.sessions?.has_matches,
          season_id: row.sessions?.season_id || null,
        }))
      ) as Promise<SessionResponse[]>;
    },
  });
  const matchesQuery = useQuery({
    queryKey: ["matches", teamId, "home", scope.key],
    enabled,
    queryFn: async () => {
      let query = supabaseV2
        .from("matches")
        .select("*, sessions!inner(season_id)")
        .eq("team_id", teamId)
        .eq("counts_for_stats", true);
      if (seasonId) query = query.eq("sessions.season_id", seasonId);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map(({ sessions, ...row }: any) => ({
        ...row,
        season_id: sessions?.season_id || null,
      })) as Match[];
    },
  });
  const matchStatsQuery = useQuery({
    queryKey: ["match-player-stats", teamId, scope.key],
    enabled,
    queryFn: async () => {
      const rows = await fetchAllPages((from, to) => {
        let query = supabaseV2
          .from("match_player_stats")
          .select(
            "id, match_id, player_id, goals, assists, yellow_cards, red_cards, started, matches!inner(team_id, starts_at, opponent_name, our_score, opponent_score, competition_id, competition_name, counts_for_stats, sessions!inner(season_id))"
          )
          .eq("matches.team_id", teamId)
          .eq("matches.counts_for_stats", true);
        if (seasonId) query = query.eq("matches.sessions.season_id", seasonId);
        return query.range(from, to);
      });
      return rows.map((row: any) => ({
        id: row.id,
        match_id: row.match_id,
        player_id: row.player_id,
        goals: row.goals || 0,
        assists: row.assists || 0,
        yellow_cards: row.yellow_cards || 0,
        red_cards: row.red_cards || 0,
        started: !!row.started,
        match_starts_at: row.matches?.starts_at || null,
        match_opponent_name: row.matches?.opponent_name || null,
        match_our_score: row.matches?.our_score ?? null,
        match_opponent_score: row.matches?.opponent_score ?? null,
        match_competition_id: row.matches?.competition_id || null,
        match_competition_name: row.matches?.competition_name || null,
        season_id: row.matches?.sessions?.season_id || null,
      })) as MatchPlayerStat[];
    },
  });
  const scoreSnapshotsQuery = useQuery({
    queryKey: ["squad-score-snapshots", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("squad_score_snapshots")
        .select("id, team_id, player_id, period_start, period_end, score")
        .eq("team_id", teamId);
      if (error) throw error;
      return (data || []) as SquadScoreSnapshot[];
    },
  });
  const matches = useMemo(() => {
    const all = matchesQuery.data || [];
    return competitionId ? all.filter((m) => m.competition_id === competitionId) : all;
  }, [matchesQuery.data, competitionId]);
  const matchStats = useMemo(() => {
    const all = matchStatsQuery.data || [];
    return competitionId ? all.filter((s) => s.match_competition_id === competitionId) : all;
  }, [matchStatsQuery.data, competitionId]);
  const attendance = useMemo(
    () => withMissingResponses(playersQuery.data || [], eventsQuery.data || [], responsesQuery.data || []),
    [playersQuery.data, eventsQuery.data, responsesQuery.data],
  );
  return {
    players: playersQuery.data || [],
    events: eventsQuery.data || [],
    responses: responsesQuery.data || [],
    /** Come `responses`, piu' una riga no_response per ogni sessione passata senza risposta. */
    attendance,
    matches,
    matchStats,
    squadScoreSnapshots: scoreSnapshotsQuery.data || [],
    isLoading:
      !enabled ||
      playersQuery.isLoading ||
      eventsQuery.isLoading ||
      responsesQuery.isLoading ||
      matchesQuery.isLoading ||
      matchStatsQuery.isLoading ||
      scoreSnapshotsQuery.isLoading,
  };
}
